import { joinTitles } from './join-titles';

export type RetryEmphasis = 'primary' | 'secondary' | 'absent';

export interface FailureCopyInput {
  failure: string | null;
  outputType: 'text' | 'canvas';
  templateName: string | null;
  sourceTitles: readonly string[];
  viewerIsRequester: boolean;
  canEdit: boolean;
}

export interface FailureCopy {
  headline: string;
  safety: string;
  reason: string | null;
  retry: RetryEmphasis;
}

interface Reason {
  reason: string;
  retry: RetryEmphasis;
}

const GENERIC_REASON = 'Something went wrong on our side.';

function headlineFor({ templateName, outputType }: FailureCopyInput): string {
  if (!templateName) return "Couldn't generate this.";
  const verb = outputType === 'canvas' ? 'draw' : 'write';
  return `Couldn't ${verb} the ${templateName.toLowerCase()}.`;
}

function reasonFor(input: FailureCopyInput): Reason {
  const sources = joinTitles(input.sourceTitles, 'or');
  if (!input.canEdit) {
    return { reason: 'Someone who can edit this document can try again.', retry: 'absent' };
  }
  switch (input.failure) {
    case 'invalid_output':
      return {
        reason:
          input.outputType === 'canvas'
            ? "The diagram didn't come out valid, even after a second try."
            : "The draft didn't fit the template's sections, even after a second try.",
        retry: 'secondary',
      };
    case 'no_structure':
      return { reason: `${sources} doesn't have the kind of structure this diagram needs.`, retry: 'absent' };
    case 'source_unavailable':
      return { reason: `${sources} can't be read any more.`, retry: 'absent' };
    case 'no_access':
      return input.viewerIsRequester
        ? { reason: `You can't open ${sources} any more.`, retry: 'absent' }
        : {
            reason: `The person who asked for this can't open ${sources} any more.`,
            retry: 'secondary',
          };
    case 'transient':
      return { reason: GENERIC_REASON, retry: 'primary' };
    default:
      return { reason: GENERIC_REASON, retry: 'secondary' };
  }
}

export function failureCopy(input: FailureCopyInput): FailureCopy {
  const { reason, retry } = reasonFor(input);
  return {
    headline: headlineFor(input),
    safety: `Nothing in ${joinTitles(input.sourceTitles, 'or')} changed.`,
    reason,
    retry,
  };
}
